import { Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { MetricInstrumentData } from 'lib-analytics-ui';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { Fragment, memo, useMemo } from 'react';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

import { DashboardLegendItem } from 'app/views/layout/views/dashboard/components/legend';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { useAppSelector } from 'utils/redux/useAppSelector';
import styles from './channelDailyConversationsMetric.module.scss';

type ChannelDashboardDailyConversationsMetricProps = PropsWithClassName;

export const ChannelDashboardDailyConversationsMetric = memo(
  function ChannelDashboardDailyConversationsMetric({
    className,
  }: ChannelDashboardDailyConversationsMetricProps): JSX.Element {
    const organization = useAppSelector(
      (state) => state.settings.organizationV2.organization,
    );

    const { metrics, channels, isSkeleton } =
      useChannelDashboardChannelContext();

    const {
      conversations_answered = 0,
      conversations_received = 0,
      conversations_abandoned,
    } = metrics ?? {};

    const noDataColor =
      !conversations_answered &&
      !conversations_received &&
      !conversations_abandoned
        ? typography.color.tertiary
        : undefined;

    const items = useMemo(
      (): DashboardLegendItem[] => [
        {
          type: 'number',
          title: 'Answered',
          format: 'number',
          value: conversations_answered ?? 0,
          color: noDataColor ?? colors.green[500],
        },
        ...(typeof conversations_abandoned !== 'undefined'
          ? ([
              {
                type: 'number',
                title: 'Abandoned',
                format: 'number',
                value: conversations_abandoned ?? 0,
                color: noDataColor ?? colors.red[500],
                linkProps: {
                  path: '/search/:scope',
                  params: createSearchUrlParams({
                    scope: 'conversations',
                    filters: {
                      strategy: 'all',
                      filters: [
                        {
                          field: 'abandoned_at',
                          operator: 'between',
                          values: [{ value: 'today' }],
                          meta: {
                            timezone: organization?.timezone ?? 'UTC',
                          },
                        },
                        {
                          field: 'initial_channel',
                          operator: 'is_one',
                          values: channels.map((channel) => ({
                            value: convertChannelCasing(channel, 'truncate'),
                          })),
                        },
                      ],
                    },
                  }),
                },
              },
            ] satisfies DashboardLegendItem[])
          : []),
        {
          type: 'number',
          title: 'Received',
          format: 'number',
          value: conversations_received ?? 0,
          color: noDataColor,
        },
      ],
      [
        channels,
        conversations_abandoned,
        conversations_answered,
        conversations_received,
        noDataColor,
      ],
    );

    return (
      <ChannelDashboardMetric
        className={className}
        title="Daily stats"
        isSkeleton={isSkeleton}
        metricType="daily_conversations"
      >
        <div className={styles.content}>
          {items.map(({ title, value, color, linkProps }) => (
            <Fragment key={title}>
              {isSkeleton ? (
                <Skeleton height="24px" width="32px" />
              ) : (
                <MetricInstrumentData
                  className={styles.value}
                  data={typeof value === 'number' ? value : 0}
                  color={color}
                  format="number"
                />
              )}

              {isSkeleton ? (
                <Skeleton height="16px" width="72px" />
              ) : (
                <Text.Paragraph as="span" variant="4" subdued>
                  {linkProps ? (
                    <AppLink {...linkProps} className={styles.link}>
                      {title}
                    </AppLink>
                  ) : (
                    title
                  )}
                </Text.Paragraph>
              )}
            </Fragment>
          ))}
        </div>
      </ChannelDashboardMetric>
    );
  },
);
